import React, { useContext, useReducer } from "react"
import { CarrotErrorCodes, getErrorMessageFromCode } from "utils/CarrotErrors"
import CarrotClient from "lib/carrot-api/index"
import { reportError, reportErrorMessage } from "utils/ErrorReporting"
import { isHttpError } from "utils/HttpErrors"
import Settings from "utils/CarrotConfig"
import { useSignUpFlow } from "../../context/signup/SignUpFlowContext"
import L10n from "lib/carrot-l10n"
import { getHeap } from "../../../utils/heap"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

interface SignUpFindAccountReducerState {
  errorMessage: string
  inputsEnabled: boolean
  shake: boolean
  showTryAgain: boolean
}

interface SignUpFindAccountProviderProps extends SignUpFindAccountReducerState {
  onSubmit: (dobVal: string) => void
}

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

const SignUpFindAccountContext = React.createContext<SignUpFindAccountProviderProps>(null)

export const useSignUpFindAccount = () => useContext(SignUpFindAccountContext)

const actionTypes = {
  error: "ERROR",
  tryAgainMessage: "TRY_AGAIN_MESSAGE",
  start: "START_REQUEST"
}

function signUpFindAccountReducer(state, action): SignUpFindAccountReducerState {
  switch (action.type) {
    case actionTypes.error: {
      return {
        ...state,
        inputsEnabled: true,
        shake: true,
        errorMessage: action.payload
      }
    }
    case actionTypes.tryAgainMessage: {
      return {
        ...state,
        showTryAgain: true,
        inputsEnabled: true,
        shake: true,
        errorMessage: null
      }
    }
    case actionTypes.start: {
      return {
        ...state,
        inputsEnabled: false,
        shake: false
      }
    }
    default: {
      throw new Error(`Unhandled type: ${action.type}`)
    }
  }
}

export function SignUpFindAccountProvider({
  handleSubmit,
  children,
  onEmailFound,
  onEmailNotFound,
  onMultipleEmployeesFound,
  onEmployeeAlreadyRegistered
}) {
  const [state, dispatch] = useReducer(signUpFindAccountReducer, {
    errorMessage: null,
    inputsEnabled: true,
    shake: false,
    showTryAgain: false
  })
  const { handleSaveMemberInfoInMemory, saveMultipleEmployeesFound } = useSignUpFlow()

  const setFormError = (errorMessage) => dispatch({ type: actionTypes.error, payload: errorMessage })
  const showTryAgain = () => dispatch({ type: actionTypes.tryAgainMessage })
  const startRequest = () => dispatch({ type: actionTypes.start })

  const setErrorState = (errorCode) => {
    setFormError(getErrorMessageFromCode(errorCode))
  }

  const onSubmit = handleSubmit(async ({ email, firstName, lastName }, dobVal) => {
    getHeap().track("NoEmailOnboarding", { EventName: "submit sign up find account" })
    startRequest()
    let memberInfo
    try {
      memberInfo = await client.signUpFirstLastDateOfBirth(email, firstName, lastName, dobVal)
    } catch (error) {
      if (isHttpError(error)) {
        const { code } = await error.response.json()
        if (code === CarrotErrorCodes.EMPLOYEE_NOT_FOUND) {
          handleSaveMemberInfoInMemory({ email, dateOfBirth: dobVal, firstName, lastName })
          showTryAgain()
          return
        }
        if (code === CarrotErrorCodes.MULTIPLE_EMPLOYEES_FOUND) {
          handleSaveMemberInfoInMemory({ email, dateOfBirth: dobVal, firstName, lastName })
          saveMultipleEmployeesFound()
          onMultipleEmployeesFound()
          return
        }
        setErrorState(code)
        return
      }

      reportError(error)
      setErrorState(CarrotErrorCodes.UNEXPECTED_ERROR)
      return
    }

    if (memberInfo.samlIdpEntityId) {
      reportErrorMessage(
        `Attempted to do a no email sign up using a SAML account with EmployeeId "${memberInfo.employeeId}"`
      )
      setFormError(`Oops, something went wrong. We'll contact you at ${memberInfo.existingEmail} to get this fixed.`)
      return
    }

    if (memberInfo.existingEmail) {
      if (!memberInfo.employeeAlreadyRegistered) {
        onEmailFound(memberInfo.existingEmail)
      } else {
        handleSaveMemberInfoInMemory({ email: memberInfo.existingEmail })
        onEmployeeAlreadyRegistered()
      }
    } else {
      onEmailNotFound(email, dobVal, firstName, lastName)
    }
  })

  return (
    <SignUpFindAccountContext.Provider value={{ ...state, onSubmit }}>{children}</SignUpFindAccountContext.Provider>
  )
}
